import { Text } from '@shared/components';
import { ActionTimelineEvent } from '@shared/components/events-timeline/components/action-timeline-event';
import { Option, Select, TextArea } from '@shared/components/react-hook-form';
import { useToast } from '@shared/components/toast';
import {
  TriggerPortalAppointmentSchedulingMutation,
  useTriggerPortalAppointmentSchedulingMutation as useSchedulePortal,
} from '@shared/generated/graphql';
import { PortalDatePolicy } from 'clerk_common/enums';
import { PortalDetails, ScheduleStop } from 'clerk_common/types';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { DateOption, generateDateOptions } from '../util';
import { ApptStatusChip } from './ApptStatusChip';
import { ScheduleButton, StopProps } from './Stop';
import { knownAndInProgress, notYetScheduledOrUnknown } from './util';

const NUM_DATE_OPTIONS = 7;

type BlueYonderStopForm = {
  day?: DateOption;
  notes: string;
};

export function BlueYonderStop({
  stop,
  index,
  shipmentId,
  refetchShipment,
}: StopProps) {
  const [submitEnabled, setSubmitEnabled] = useState<boolean>(true);
  const { sendToast } = useToast();
  const defaultValues: BlueYonderStopForm = getDefaultValues(stop);
  const methods = useForm<BlueYonderStopForm>({ defaultValues });
  const { control, getValues } = methods;

  const options = { onCompleted: onPortalCompleted };
  const [triggerPortal] = useSchedulePortal(options);

  const status = stop.appointmentStatus;
  const isNotYetScheduled = notYetScheduledOrUnknown(status);
  const isInProgress = knownAndInProgress(status);

  const dateOptions = generateDateAndPortalOptions(stop.day, NUM_DATE_OPTIONS);
  const defaultDate = dateOptions[0];

  async function scheduleStop() {
    const stopsIndexToSchedule = index;
    const day = getValues('day');
    if (!day) {
      sendToast('Could not request unknown date.');
      return;
    }
    const portalDatePolicy = getDatePolicy(day);
    const details: PortalDetails = {
      datePolicy: portalDatePolicy,
      scheduleNotes: getValues('notes'),
    };
    if (portalDatePolicy === PortalDatePolicy.USE_SPECIFIED_DATE) {
      details.day = day.value;
    }
    const input = {
      shipmentId,
      stopsIndexToSchedule,
      movementStopId: stop.movementStopId,
      details,
    };
    setSubmitEnabled(false);
    await triggerPortal({ variables: { input } });
  }

  async function onPortalCompleted(
    data: TriggerPortalAppointmentSchedulingMutation
  ) {
    const { success, error } = data.triggerPortalAppointmentScheduling;
    if (!success && error) {
      sendToast(error);
    }
    if (success) {
      await refetchShipment();
    }
    setSubmitEnabled(true);
  }

  // TODO(jonathan): disable schedule button if form is not valid
  return (
    <FormProvider {...methods}>
      <ActionTimelineEvent type={stop.stopType}>
        <div className="mb-4 ml-2 mt-1 text-gray-700">
          <Text isHeavy type="body-xs">
            {stop.locationLabel}
          </Text>
          <Text type="body-xs">{stop.address.addressOne}</Text>
          {isNotYetScheduled && (
            <>
              <div className="my-3 rounded-md border bg-gray-50 p-2">
                <Text type="body-sm" className="mb-3">
                  Blue Yonder
                </Text>
                <Select
                  name="day"
                  defaultValue={defaultDate}
                  renderValue={(o: DateOption) => o.label}
                  className="mb-1"
                  control={control}
                  onChange={() => {}}
                >
                  {dateOptions.map((o) => (
                    <Option value={o} key={o.value}>
                      {o.label}
                    </Option>
                  ))}
                </Select>
                <Text type="body-sm" className="mt-2 text-gray-500">
                  Notes
                </Text>
                <TextArea type="body-sm" control={control} name="notes" />
              </div>
              <div>
                <ScheduleButton
                  label="Request Appointment"
                  onPress={scheduleStop}
                  disabled={!submitEnabled}
                />
              </div>
            </>
          )}
          {isInProgress && <ApptStatusChip stop={stop} />}
        </div>
      </ActionTimelineEvent>
    </FormProvider>
  );
}

function getDefaultValues(stop: ScheduleStop): BlueYonderStopForm {
  const values: BlueYonderStopForm = {
    notes: stop.scheduleNotes ?? '',
  };
  return values;
}

function generateDateAndPortalOptions(
  startDate: string | undefined,
  count: number
): DateOption[] {
  const dateOptions = generateDateOptions(startDate, count);
  dateOptions.push({
    value: PortalDatePolicy.USE_PORTAL_DATE,
    label: 'Use Blue Yonder date',
  });
  return dateOptions;
}

function getDatePolicy(day: DateOption): PortalDatePolicy {
  if (day.value === PortalDatePolicy.USE_PORTAL_DATE) {
    return PortalDatePolicy.USE_PORTAL_DATE;
  }
  return PortalDatePolicy.USE_SPECIFIED_DATE;
}
